import { useState, useMemo, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useSession } from '@/context/AuthContext'
import { Link } from 'react-router-dom'
import { ArrowLeft, Plus, CheckCircle2, Clock, Circle, User, Loader2, CalendarClock, Lock, ChevronDown, Check, X, FileText, Trash2, Users2, UserPlus, Pencil, Grip, Search } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { TaskItem } from '@/components/TaskItem'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { EditProjectModal } from '@/components/EditProjectModal'
import { deptColor } from '@/lib/departments'
import { useClickOutside } from '@/lib/useClickOutside'
import { useRegisteredUsers } from '@/lib/useRegisteredUsers'
import { useToast } from '@/context/ToastContext'
import { effectiveTaskStatus, picColor, formatDate, isPic } from '@/lib/utils'
import { canSeeAllInDepartment } from '@/lib/roles'
import type { TaskStatus, ProjectNote, Task } from '@/lib/types'

type FilterValue = TaskStatus | 'all'

const FILTERS: { value: FilterValue; label: string }[] = [
  { value: 'all', label: 'Semua' },
  { value: 'not-started', label: 'Not Started' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
]

function MiniDonut({ pct }: { pct: number }) {
  return (
    <div className="relative w-14 h-14 flex-shrink-0">
      <div
        className="w-full h-full rounded-full"
        style={{
          background: `conic-gradient(#16A34A 0% ${pct}%, #E2E8F0 ${pct}% 100%)`,
        }}
      />
      <div className="absolute inset-[10px] rounded-full bg-white flex items-center justify-center">
        <span className="font-display text-[11px] font-bold leading-none" style={{ color: '#16A34A' }}>{pct}%</span>
      </div>
    </div>
  )
}

// Dropdown ubah PIC project dari daftar user terdaftar (+ opsi hapus).
function PicEditDropdown({
  current,
  onSelect,
  onClose,
}: {
  current: string | null
  onSelect: (value: string) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(true)
  const [q, setQ] = useState('')
  useClickOutside(ref, () => { setOpen(false); onClose() }, open)
  const { users } = useRegisteredUsers()

  const names = Array.from(
    new Set([current ?? '', ...users.map((u) => u.username)].filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b))

  // Kotak cari baru muncul kalau daftarnya cukup panjang untuk perlu dicari.
  // Di bawah itu, kotak cari cuma menambah satu langkah untuk daftar yang sudah
  // habis terbaca sekali pandang.
  const pakaiCari = names.length > 5
  const qLower = q.trim().toLowerCase()
  // PIC yang sedang terpasang selalu ikut tampil walau tidak cocok ketikan, supaya
  // posisinya tetap terlihat dan bisa dipilih ulang tanpa menghapus ketikan dulu.
  const shown = qLower
    ? names.filter((n) => n.toLowerCase().includes(qLower) || n === current)
    : names

  // Fokus langsung ke kotak cari begitu dropdown terbuka: dropdown ini dibuka
  // justru untuk mengganti orang, jadi mengetik adalah langkah berikutnya.
  useEffect(() => {
    if (open && pakaiCari) searchRef.current?.focus()
  }, [open, pakaiCari])

  return (
    <div ref={ref} className="relative mt-1 flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center justify-between gap-2 w-48 px-3 py-1.5 bg-[#F8F7F3] border border-[#C8C7BF] rounded-xl font-body text-[#1A1916] hover:border-[#1A1916] focus:outline-none focus:border-[#1A1916] transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          {current ? (
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
              style={{ backgroundColor: picColor(current) }}
            >
              {current.charAt(0).toUpperCase()}
            </span>
          ) : (
            <User className="w-3.5 h-3.5 text-[#AEADA8] flex-shrink-0" />
          )}
          <span className="text-xs truncate">{current || 'Pilih PIC'}</span>
        </span>
        <ChevronDown className={`w-3 h-3 text-[#AEADA8] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div role="listbox" className="absolute left-0 top-[calc(100%+4px)] z-30 min-w-[220px] bg-white border border-[#E0DFD8] rounded-xl shadow-xl py-1.5 animate-fade-in">
          {pakaiCari && (
            <div className="px-2 pb-1.5 mb-1 border-b border-[#F2F1EC]">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#C0BDB6] pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    e.stopPropagation()
                    if (e.key === 'Escape') { setOpen(false); onClose() }
                    // Enter memilih kalau hasilnya tinggal satu: cari lalu langsung pilih.
                    if (e.key === 'Enter' && shown.length === 1) { e.preventDefault(); onSelect(shown[0]) }
                  }}
                  placeholder="Cari nama"
                  className="w-full pl-7 pr-2.5 py-1.5 bg-white border border-[#E8E7E3] rounded-lg text-[11px] font-body text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:border-[#C7D2FE] transition-colors"
                />
              </div>
            </div>
          )}
          <div className="max-h-56 overflow-auto">
          {names.length === 0 && (
            <p className="px-3.5 py-2 text-xs text-[#AEADA8] font-body">Belum ada user terdaftar</p>
          )}
          {names.length > 0 && shown.length === 0 && (
            <p className="px-3.5 py-2 text-xs text-[#AEADA8] font-body">Tidak ada nama yang cocok</p>
          )}
          {shown.map((name) => (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={name === current}
              onClick={() => onSelect(name)}
              className={`w-full flex items-center gap-2 px-3.5 py-2 text-xs font-body transition-colors ${
                name === current ? 'bg-[#F4F3F0] text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'
              }`}
            >
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                style={{ backgroundColor: picColor(name) }}
              >
                {name.charAt(0).toUpperCase()}
              </span>
              <span className="flex-1 text-left truncate">{name}</span>
              {name === current && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
            </button>
          ))}
          </div>
          {current && (
            <button
              type="button"
              onClick={() => onSelect('')}
              className="w-full flex items-center gap-2 px-3.5 py-2 mt-1 pt-2 border-t border-[#F2F1EC] text-xs font-body text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
            >
              <X className="w-3.5 h-3.5 flex-shrink-0" /> Hapus PIC
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default function ProjectPage() {
  const { id } = useParams()
  const { getProject, getProjectTasks, getTaskSubtasks, createTask, updateProject, getProjectLock, assignPic, reorderTasks } = useApp()
  const { users: registeredUsers } = useRegisteredUsers()
  const { data: session } = useSession()
  const { toast } = useToast()
  const me = session?.user?.name ?? ''
  const meEmail = session?.user?.email ?? ''
  const [showCreateTask, setShowCreateTask] = useState(false)
  const [showEditProject, setShowEditProject] = useState(false)
  const [showAllTasks, setShowAllTasks] = useState(false)
  // Level akses viewer: 'full' (kelola semua) / 'assignee' (cuma item miliknya) / 'none'
  const [access, setAccess] = useState<'full' | 'viewer' | 'assignee' | 'none'>('full')
  const [filter, setFilter] = useState<FilterValue>('all')
  const [quickTitle, setQuickTitle] = useState('')
  const [quickSaving, setQuickSaving] = useState(false)
  const [editingPic, setEditingPic] = useState(false)
  const [editingDate, setEditingDate] = useState(false)
  const [editingStart, setEditingStart] = useState(false)
  const [notes, setNotes] = useState<ProjectNote[]>([])
  const [noteContent, setNoteContent] = useState('')
  const [noteDate, setNoteDate] = useState(() => new Date().toLocaleDateString('en-CA'))
  const [savingNote, setSavingNote] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all')
  const [dateSort, setDateSort] = useState<'manual' | 'newest' | 'oldest'>('manual')
  // Drag-reorder manual
  const [dragArmId, setDragArmId] = useState<string | null>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)
  // Kolaborator lintas departemen
  const [members, setMembers] = useState<{ email: string; name: string; department: string | null }[]>([])
  const [allUsers, setAllUsers] = useState<{ email: string; username: string; department: string | null }[]>([])
  const [collabEmail, setCollabEmail] = useState('')
  const [collabBusy, setCollabBusy] = useState(false)
  const [collabOpen, setCollabOpen] = useState(false)
  const collabRef = useRef<HTMLDivElement>(null)
  useClickOutside(collabRef, () => setCollabOpen(false), collabOpen)

  const projectId = id as string
  const project = getProject(projectId)
  const allTasks = getProjectTasks(projectId)

  const stats = useMemo(() => {
    // Task RUTIN (repetitif) dikecualikan dari perhitungan progres (di luar %).
    const counted = allTasks.filter((t) => !t.recurrence)
    const statuses = counted.map((t) => effectiveTaskStatus(t.status, getTaskSubtasks(t.id)))
    return {
      total: counted.length,
      notStarted: statuses.filter((s) => s === 'not-started').length,
      inProgress: statuses.filter((s) => s === 'in-progress').length,
      done: statuses.filter((s) => s === 'done').length,
    }
  }, [allTasks, getTaskSubtasks])

  const filteredTasks = useMemo(() => {
    let list = filter === 'all'
      ? allTasks
      : allTasks.filter((t) => effectiveTaskStatus(t.status, getTaskSubtasks(t.id)) === filter)
    if (priorityFilter !== 'all') list = list.filter((t) => t.priority === priorityFilter)
    list = [...list].sort((a, b) => {
      if (dateSort === 'manual') {
        const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER
        const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER
        if (ao !== bo) return ao - bo
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }
      const da = new Date(a.createdAt).getTime()
      const db = new Date(b.createdAt).getTime()
      return dateSort === 'newest' ? db - da : da - db
    })
    return list
  }, [allTasks, filter, priorityFilter, dateSort, getTaskSubtasks])

  const pct = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0
  const lock = getProjectLock(projectId)

  // Akses: 'full' = pemegang/pemilik/admin → kelola semua. 'assignee' = hanya item
  // miliknya. 'none' = tidak berwenang (tombol nonaktif). Sumber: /api/.../access.
  const myUsername = session?.user?.username ?? null
  const assigneeOnly = access === 'assignee'
  const canModify = access === 'full' // kelola project (edit/hapus/deadline/kolaborator/tambah task)
  // PIC task bisa lebih dari satu (digabung ';'), jadi cek keanggotaan daftar,
  // bukan kesamaan string. Perbandingan persis membuat PIC kedua kehilangan akses edit.
  const canEditTaskRow = (t: Task) => access === 'full' || (access === 'assignee' && isPic(t.assignee, myUsername))

  // Siapa yang boleh mengganti PIC ditentukan SERVER, bukan di sini.
  //
  // Dulu baris ini memakai aturannya sendiri, "yang sudah punya PIC hanya boleh
  // dipindah oleh PIC itu sendiri", dan aturan itu lebih ketat daripada server.
  // Akibatnya Head dan Sub-Head yang memegang project departemennya tidak pernah
  // melihat kontrolnya sama sekali, padahal server mengizinkan mereka; Director pun
  // ikut terkunci. Kontrol yang hilang tanpa penjelasan lebih buruk daripada kontrol
  // yang menolak dengan alasan.
  //
  // Sekarang kontrolnya muncul untuk siapa pun yang mengelola project ini atau
  // memegang PIC-nya, dan penolakan server ditampilkan apa adanya di savePic.
  // Aturan wewenangnya tetap satu tempat: routes/projects.ts.
  //
  // Satu pagar level ikut dicerminkan di sini, bukan cuma dipakai menyembunyikan:
  // Staff tidak mengubah PIC, termasuk Staff yang sedang memegang project itu.
  // Baginya badge PIC jadi tampilan saja. Server menolak hal yang sama, jadi UI dan
  // server tidak bisa berbeda pendapat.
  const isProjectPic = !!myUsername && !!project?.pic
    && project.pic.trim().toLowerCase() === myUsername.trim().toLowerCase()
  const bolehAturPic = canSeeAllInDepartment(session?.user?.role)
  const canChangePic = bolehAturPic && (canModify || isProjectPic)

  // Drag-reorder hanya saat urutan manual + filter Semua + tidak terkunci + bukan assignee-only.
  const canReorder = dateSort === 'manual' && filter === 'all' && !lock.locked && !assigneeOnly
  const resetDrag = () => { setDragArmId(null); setDragIndex(null); setOverIndex(null) }
  const handleDrop = () => {
    if (dragIndex === null || overIndex === null || dragIndex === overIndex) { resetDrag(); return }
    const arr = [...filteredTasks]
    const [moved] = arr.splice(dragIndex, 1)
    arr.splice(overIndex, 0, moved)
    reorderTasks(arr.map((t) => t.id))
    resetDrag()
  }

  const handleQuickAdd = async () => {
    const t = quickTitle.trim()
    if (!t || quickSaving || lock.locked || !canModify) return
    setQuickSaving(true)
    try {
      await createTask(projectId, t, '', 'medium')
      setQuickTitle('')
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Gagal membuat task')
    } finally {
      setQuickSaving(false)
    }
  }

  const savePic = async (value: string) => {
    setEditingPic(false)
    const name = value.trim()
    if (!name) {
      await updateProject(projectId, { pic: null })
      return
    }
    // Pemindahan PIC tidak lagi meminta alasan. Dulu di sini ada window.prompt yang
    // mewajibkannya, dan itu dua masalah sekaligus: kewajibannya sendiri, dan dialog
    // bawaan browser yang boleh dibungkam browser sehingga pemindahan batal diam-diam.
    // Perpindahannya tetap tercatat di reassignment_log, hanya kolom alasannya kosong.
    const u = registeredUsers.find((x) => x.username === name)
    const email = u?.email ?? (name === me ? meEmail : null)
    // Penolakan server ditampilkan. Tanpa ini, assignPic yang gagal melempar ke
    // promise tanpa penangkap: PIC tidak berpindah dan layar diam saja.
    try {
      if (email) {
        await assignPic(projectId, email, name)
      } else {
        await updateProject(projectId, { pic: name })
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Gagal mengubah PIC project.', 'error')
    }
  }

  const saveDeadline = (value: string) => {
    updateProject(projectId, { deadline: value || null })
    setEditingDate(false)
  }

  // Set tanggal MULAI project → mengubah created_at (boleh backdate ke masa lalu)
  const saveStart = (value: string) => {
    if (value) updateProject(projectId, { createdAt: value })
    setEditingStart(false)
  }

  // ── Catatan / notulen project ──
  useEffect(() => {
    if (!projectId) return
    fetch(`/api/projects/${projectId}/notes`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setNotes(Array.isArray(d) ? d : []))
      .catch(() => {})
  }, [projectId])

  // ── Kolaborator lintas departemen ──
  useEffect(() => {
    if (!projectId) return
    fetch(`/api/projects/${projectId}/members`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setMembers(Array.isArray(d) ? d : []))
      .catch(() => {})
  }, [projectId])

  useEffect(() => {
    fetch('/api/users/all')
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setAllUsers(Array.isArray(d) ? d : []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!projectId) return
    fetch(`/api/projects/${projectId}/access`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.access) setAccess(d.access) })
      .catch(() => {})
  }, [projectId])

  const addCollab = async () => {
    if (!collabEmail || collabBusy) return
    setCollabBusy(true)
    try {
      const res = await fetch(`/api/projects/${projectId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: collabEmail }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error ?? 'Gagal menambah kolaborator')
      setMembers((prev) => [...prev, { email: data.email, name: data.name, department: data.department }])
      setCollabEmail('')
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Gagal menambah kolaborator')
    } finally {
      setCollabBusy(false)
    }
  }

  const removeCollab = async (email: string) => {
    setMembers((prev) => prev.filter((m) => m.email !== email))
    await fetch(`/api/projects/${projectId}/members?email=${encodeURIComponent(email)}`, { method: 'DELETE' }).catch(() => {})
  }

  const addNote = async () => {
    const content = noteContent.trim()
    if (!content || savingNote) return
    setSavingNote(true)
    try {
      const res = await fetch(`/api/projects/${projectId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, author: me || null, noteDate }),
      })
      if (res.ok) {
        const note: ProjectNote = await res.json()
        setNotes((prev) => [note, ...prev].sort((a, b) => b.noteDate.localeCompare(a.noteDate)))
        setNoteContent('')
      }
    } finally {
      setSavingNote(false)
    }
  }

  const deleteNote = async (noteId: string) => {
    setNotes((prev) => prev.filter((n) => n.id !== noteId))
    await fetch(`/api/notes/${noteId}`, { method: 'DELETE' }).catch(() => {})
  }

  const fmtNoteDate = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })

  if (!project) {
    return (
      <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#8A8A85] font-body mb-4 text-sm">Project tidak ditemukan</p>
          <Link to="/" className="text-sm font-body font-semibold text-[#1A1916] hover:opacity-50">
            ← Kembali
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F5F4F0]">

      {/* Header */}
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/projects" className="flex items-center gap-1.5 text-[#8A8A85] hover:text-[#1A1916] text-sm font-body transition-colors flex-shrink-0">
              <ArrowLeft className="w-3.5 h-3.5" />
              Projects
            </Link>
            <span className="text-[#C8C7BF] flex-shrink-0">/</span>
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: project.color }} />
              <span className="text-[#1A1916] text-sm font-body font-medium truncate">{project.name}</span>
            </div>
          </div>
          {lock.locked ? (
            <span className="flex-shrink-0 flex items-center gap-1.5 px-4 py-1.5 bg-[#F4F3F0] text-[#AEADA8] rounded-xl text-sm font-medium font-body cursor-not-allowed">
              <Lock className="w-3.5 h-3.5" /> Terkunci
            </span>
          ) : canModify ? (
            <button
              onClick={() => setShowCreateTask(true)}
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-1.5 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A] active:scale-[0.98] transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              Tambah Task
            </button>
          ) : null}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">

        {/* Project hero */}
        <div className="bg-white border border-[#E8E7E3] rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-5">
            {/* Color bar */}
            <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: project.color }} />

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="font-display text-[32px] font-bold text-[#1A1916] leading-tight">
                    {project.name}
                  </h1>
                  {canModify && (
                    <button
                      onClick={() => setShowEditProject(true)}
                      title="Edit project (nama, deskripsi, warna)"
                      aria-label="Edit project"
                      className="flex-shrink-0 p-1.5 rounded-lg text-[#C8C7BF] hover:text-[#1A1916] hover:bg-[#F4F3F0] transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {/* PIC badge — klik untuk ubah/hapus */}
                {editingPic ? (
                  <PicEditDropdown
                    current={project.pic}
                    onSelect={savePic}
                    onClose={() => setEditingPic(false)}
                  />
                ) : project.pic ? (
                  <div className="flex items-center gap-2 mt-1 flex-shrink-0">
                    {canChangePic ? (
                      <button
                        onClick={() => setEditingPic(true)}
                        title="Klik untuk ubah PIC"
                        className="flex items-center gap-2 bg-[#F4F3F0] border border-[#E8E7E3] rounded-xl px-3 py-1.5 hover:border-[#C8C7BF] transition-colors"
                      >
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: picColor(project.pic) }}>
                          {project.pic.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-left">
                          <p className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body leading-none mb-0.5">PIC</p>
                          <p className="text-xs font-body font-semibold text-[#1A1916]">{project.pic}</p>
                        </div>
                      </button>
                    ) : (
                      <div
                        title="PIC project hanya bisa diubah oleh Sub-Head ke atas"
                        className="flex items-center gap-2 bg-[#F4F3F0] border border-[#E8E7E3] rounded-xl px-3 py-1.5"
                      >
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: picColor(project.pic) }}>
                          {project.pic.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-left">
                          <p className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body leading-none mb-0.5">PIC</p>
                          <p className="text-xs font-body font-semibold text-[#1A1916]">{project.pic}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : canChangePic ? (
                  <button
                    onClick={() => setEditingPic(true)}
                    className="flex items-center gap-1.5 text-[#6366F1] hover:text-[#4338CA] border border-dashed border-[#C7D2FE] hover:border-[#6366F1] rounded-xl px-3 py-1.5 mt-1 flex-shrink-0 transition-colors"
                  >
                    <User className="w-3.5 h-3.5" />
                    <span className="text-xs font-body font-medium">Set PIC</span>
                  </button>
                ) : null}
              </div>
              {project.description && (
                <p className="text-sm text-[#8A8A85] font-body leading-relaxed max-w-2xl">
                  {project.description}
                </p>
              )}

              {/* Stats row */}
              <div className="flex items-center gap-6 mt-5">
                <div className="flex items-center gap-2">
                  <Circle className="w-3.5 h-3.5 text-[#94A3B8]" />
                  <span className="text-sm font-body text-[#8A8A85]">
                    <span className="font-display font-bold text-[#94A3B8]">{stats.notStarted}</span>{' '}
                    Not Started
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-[#F59E0B]" />
                  <span className="text-sm font-body text-[#8A8A85]">
                    <span className="font-display font-bold text-[#F59E0B]">{stats.inProgress}</span>{' '}
                    In Progress
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#16A34A]" />
                  <span className="text-sm font-body text-[#8A8A85]">
                    <span className="font-display font-bold text-[#16A34A]">{stats.done}</span>{' '}
                    Done
                  </span>
                </div>

                {/* Tanggal mulai + deadline project — klik untuk set/ubah */}
                <div className="flex items-center gap-2 ml-auto flex-wrap justify-end">
                  {/* Tanggal mulai (created_at) — selalu ada, bisa di-backdate */}
                  {editingStart ? (
                    <input
                      type="date"
                      autoFocus
                      defaultValue={project.createdAt?.slice(0, 10) ?? ''}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveStart((e.target as HTMLInputElement).value)
                        if (e.key === 'Escape') setEditingStart(false)
                      }}
                      onBlur={(e) => saveStart(e.target.value)}
                      className="text-xs px-2.5 py-1.5 bg-[#F8F7F3] border border-[#C8C7BF] rounded-lg font-body text-[#1A1916] focus:outline-none focus:border-[#1A1916]"
                    />
                  ) : (
                    <button
                      onClick={() => setEditingStart(true)}
                      title="Klik untuk ubah tanggal mulai project"
                      className="flex items-center gap-1.5 text-xs font-body font-medium px-2.5 py-1.5 rounded-lg bg-[#F4F3F0] text-[#6B6860] hover:bg-[#EEEEE9] transition-colors"
                    >
                      <CalendarClock className="w-3.5 h-3.5" />
                      Mulai · {formatDate(project.createdAt)}
                    </button>
                  )}

                  {editingDate ? (
                    <input
                      type="date"
                      autoFocus
                      defaultValue={project.deadline?.slice(0, 10) ?? ''}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveDeadline((e.target as HTMLInputElement).value)
                        if (e.key === 'Escape') setEditingDate(false)
                      }}
                      onBlur={(e) => saveDeadline(e.target.value)}
                      className="text-xs px-2.5 py-1.5 bg-[#F8F7F3] border border-[#C8C7BF] rounded-lg font-body text-[#1A1916] focus:outline-none focus:border-[#1A1916]"
                    />
                  ) : project.deadline ? (
                    (() => {
                      const past = new Date(project.deadline) < new Date(new Date().setHours(0, 0, 0, 0)) && pct < 100
                      return (
                        <button
                          onClick={() => setEditingDate(true)}
                          title="Klik untuk ubah deadline"
                          className={`flex items-center gap-1.5 text-xs font-body font-medium px-2.5 py-1.5 rounded-lg transition-colors ${
                            past ? 'bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]' : 'bg-[#F4F3F0] text-[#6B6860] hover:bg-[#EEEEE9]'
                          }`}
                        >
                          <CalendarClock className="w-3.5 h-3.5" />
                          {past ? 'Telat · ' : 'Deadline · '}{formatDate(project.deadline)}
                        </button>
                      )
                    })()
                  ) : (
                    <button
                      onClick={() => setEditingDate(true)}
                      className="flex items-center gap-1.5 text-xs font-body font-medium text-[#6366F1] hover:text-[#4338CA] border border-dashed border-[#C7D2FE] hover:border-[#6366F1] px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      <CalendarClock className="w-3.5 h-3.5" /> Set deadline
                    </button>
                  )}
                </div>
              </div>

              {/* Kolaborator — gaya Jira: tumpukan avatar + popover lihat/kelola */}
              <div className="relative mt-4 w-fit" ref={collabRef}>
                <button
                  onClick={() => setCollabOpen((o) => !o)}
                  title="Lihat & kelola kolaborator"
                  className="flex items-center gap-2"
                >
                  {members.length === 0 ? (
                    <span className="flex items-center gap-1.5 text-xs font-body font-medium text-[#6366F1] border border-dashed border-[#C7D2FE] hover:border-[#6366F1] rounded-xl px-2.5 py-1.5 transition-colors">
                      <UserPlus className="w-3.5 h-3.5" /> Kolaborator
                    </span>
                  ) : (
                    <>
                      <span className="flex items-center">
                        {members.slice(0, 4).map((m, idx) => (
                          <span
                            key={m.email}
                            title={m.department && m.department !== project.department ? `${m.name} · ${m.department}` : m.name}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white ring-2 ring-white"
                            style={{ backgroundColor: picColor(m.name || m.email), marginLeft: idx === 0 ? 0 : -8 }}
                          >
                            {(m.name || m.email).charAt(0).toUpperCase()}
                          </span>
                        ))}
                        {members.length > 4 && (
                          <span className="w-7 h-7 rounded-full bg-[#F4F3F0] text-[#6B6860] flex items-center justify-center text-[10px] font-bold ring-2 ring-white" style={{ marginLeft: -8 }}>
                            +{members.length - 4}
                          </span>
                        )}
                      </span>
                      {(() => {
                        const depts = Array.from(new Set(members.map((m) => m.department).filter((d): d is string => !!d && d !== project.department)))
                        return depts.map((dep) => {
                          const c = deptColor(dep)
                          return (
                            <span key={dep} className="text-[10px] font-body font-semibold px-1.5 py-0.5 rounded-md border" style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>
                              {dep}
                            </span>
                          )
                        })
                      })()}
                      <span
                        className="w-7 h-7 rounded-full border border-dashed border-[#C8C7BF] flex items-center justify-center text-[#8A8A85] hover:text-[#6366F1] hover:border-[#6366F1] transition-colors"
                        style={{ marginLeft: members.length > 0 ? -8 : 0 }}
                      >
                        <Users2 className="w-3.5 h-3.5" />
                      </span>
                    </>
                  )}
                </button>

                {collabOpen && (
                  <div className="absolute left-0 top-[calc(100%+8px)] z-30 w-[360px] max-h-[60vh] overflow-y-auto bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 p-4 animate-fade-in">
                    <div className="flex items-center gap-2 mb-2">
                      <Users2 className="w-4 h-4 text-[#6B6860]" />
                      <h3 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold">Kolaborator</h3>
                    </div>
                    <p className="text-[11px] text-[#AEADA8] font-body mb-3 leading-relaxed">
                      Tambahkan orang (dari departemen mana pun) ke project ini. Mereka bisa melihat & mengerjakan project ini. Kepemilikan tetap di {project.department ?? 'departemen asal'}.
                    </p>
                    {canModify && (
                      <div className="flex items-center gap-2 mb-2">
                        <select
                          value={collabEmail}
                          onChange={(e) => setCollabEmail(e.target.value)}
                          disabled={collabBusy}
                          className="flex-1 min-w-0 text-xs px-2.5 py-1.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-lg font-body text-[#1A1916] focus:outline-none focus:border-[#1A1916] cursor-pointer"
                        >
                          <option value="">Pilih orang…</option>
                          {allUsers
                            .filter((u) => !members.some((m) => m.email.toLowerCase() === u.email.toLowerCase()))
                            .map((u) => <option key={u.email} value={u.email}>{u.department && u.department !== project.department ? `${u.username} · ${u.department}` : u.username}</option>)}
                        </select>
                        <button onClick={addCollab} disabled={!collabEmail || collabBusy}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-[#1A1916] text-white rounded-lg text-xs font-medium font-body hover:bg-[#2E2E2A] disabled:opacity-40 transition-all flex-shrink-0">
                          {collabBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}
                    {members.length === 0 ? (
                      <p className="text-[11px] text-[#AEADA8] font-body">Belum ada.</p>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {members.map((m) => (
                          <span key={m.email} className="inline-flex items-center gap-2 px-1.5 py-1 bg-[#F4F3F0] border border-[#E8E7E3] rounded-xl">
                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: picColor(m.name || m.email) }}>
                              {(m.name || m.email).charAt(0).toUpperCase()}
                            </span>
                            <span className="text-xs font-body text-[#1A1916] flex-1 truncate">{m.name}</span>
                            {m.department && m.department !== project.department && (() => {
                              const c = deptColor(m.department)
                              return (
                                <span className="text-[10px] font-body font-semibold px-1.5 py-0.5 rounded-md border flex-shrink-0"
                                  style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>
                                  {m.department}
                                </span>
                              )
                            })()}
                            {canModify && (
                              <button onClick={() => removeCollab(m.email)} title="Hapus" className="text-[#C0BDB6] hover:text-[#DC2626] transition-colors flex-shrink-0">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Mini donut */}
            <div className="flex-shrink-0 flex flex-col items-center gap-2">
              <MiniDonut pct={pct} />
              <span className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body">Selesai</span>
            </div>
          </div>

          {/* Progress bar */}
          {stats.total > 0 && (
            <div className="mt-5 ml-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[9px] uppercase tracking-[0.15em] text-[#C0BDB6] font-body">
                  {stats.total} task total
                </span>
                <span className="text-[11px] text-[#6B6860] font-body font-semibold">{pct}%</span>
              </div>
              <div className="h-1.5 bg-[#F0EFE9] rounded-full overflow-hidden flex">
                {stats.done > 0 && (
                  <div
                    className="h-full transition-all duration-700"
                    style={{ width: `${(stats.done / stats.total) * 100}%`, backgroundColor: project.color }}
                  />
                )}
                {stats.inProgress > 0 && (
                  <div
                    className="h-full bg-[#C8C7BF] transition-all duration-700"
                    style={{ width: `${(stats.inProgress / stats.total) * 100}%` }}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Banner terkunci oleh dependency */}
        {lock.locked && lock.blocker && (
          <div className="flex items-start gap-3 px-4 py-3.5 bg-[#FEF3C7] border border-[#FDE68A] rounded-2xl mb-5">
            <Lock className="w-4 h-4 text-[#B45309] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-body font-semibold text-[#92400E]">
                Project terkunci
              </p>
              <p className="text-xs text-[#B45309] font-body leading-relaxed mt-0.5">
                Selesaikan project{' '}
                <Link to={`/projects/${lock.blocker.id}`} className="font-semibold underline hover:opacity-70">
                  {lock.blocker.name}
                </Link>{' '}
                dulu. Task & subtask di project ini tidak bisa diubah sampai project itu 100% selesai.
              </p>
            </div>
          </div>
        )}

        {/* ── CATATAN & NOTULEN ── */}
        <div className="bg-white border border-[#E8E7E3] rounded-2xl mb-5 overflow-hidden">
          {/* Header — klik untuk buka/tutup */}
          <button
            onClick={() => setNotesOpen((o) => !o)}
            aria-expanded={notesOpen}
            className="w-full flex items-center gap-2 px-5 py-4 hover:bg-[#FAFAF8] transition-colors text-left"
          >
            <FileText className="w-4 h-4 text-[#6B6860]" />
            <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold">Catatan &amp; Notulen</h2>
            <span className="text-[10px] text-[#C0BDB6] font-body">{notes.length}</span>
            <ChevronDown className={`ml-auto w-4 h-4 text-[#AEADA8] transition-transform ${notesOpen ? 'rotate-180' : ''}`} />
          </button>

          {notesOpen && (
          <div className="px-5 pb-5">
          {/* Form tambah catatan */}
          <div className="flex flex-col gap-2 mb-4">
            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Tulis catatan rapat, feedback, atau keputusan penting…"
              rows={3}
              className="w-full text-sm px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl font-body text-[#1A1916] placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#1A1916] focus:bg-white resize-none leading-relaxed"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-[11px] font-body text-[#8A8A85]">
                <CalendarClock className="w-3.5 h-3.5 text-[#AEADA8]" />
                <input
                  type="date"
                  value={noteDate}
                  onChange={(e) => setNoteDate(e.target.value)}
                  title="Tanggal catatan (default hari ini, bisa diubah)"
                  className="text-xs px-2 py-1.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-lg font-body text-[#1A1916] focus:outline-none focus:border-[#1A1916]"
                />
              </label>
              <button
                onClick={addNote}
                disabled={!noteContent.trim() || savingNote}
                className="ml-auto flex items-center gap-2 px-4 py-2 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {savingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Tambah Catatan
              </button>
            </div>
          </div>

          {/* Riwayat catatan (timeline) */}
          {notes.length === 0 ? (
            <p className="text-[12px] text-[#AEADA8] font-body text-center py-4 border-t border-[#F4F3F0]">
              Belum ada catatan. Tambahkan notulen rapat atau feedback di atas.
            </p>
          ) : (
            <ol className="border-t border-[#F4F3F0] pt-4">
              {notes.map((n) => (
                <li key={n.id} className="flex gap-3 group">
                  <div className="flex flex-col items-center pt-1">
                    <span className="w-2 h-2 rounded-full bg-[#6366F1] flex-shrink-0" />
                    <span className="flex-1 w-px bg-[#F0EFE9] mt-1" />
                  </div>
                  <div className="flex-1 min-w-0 pb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-body font-semibold text-[#1A1916]">{fmtNoteDate(n.noteDate)}</span>
                      {n.author && <span className="text-[10px] text-[#AEADA8] font-body truncate">· {n.author}</span>}
                      <button
                        onClick={() => deleteNote(n.id)}
                        title="Hapus catatan"
                        className="ml-auto p-1 rounded-lg text-[#C8C7BF] hover:text-[#DC2626] hover:bg-[#FEF2F2] opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-[13px] text-[#3A3A36] font-body whitespace-pre-wrap leading-relaxed mt-0.5">{n.content}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
          </div>
          )}
        </div>

        {/* Filter toolbar */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <div className="flex items-center gap-1 bg-white border border-[#E8E7E3] rounded-xl p-1 w-fit">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-body font-medium transition-all duration-150 ${
                  filter === f.value
                    ? 'bg-[#1A1916] text-white shadow-sm'
                    : 'text-[#8A8A85] hover:text-[#1A1916]'
                }`}
              >
                {f.label}
                <span className={`ml-1.5 text-[10px] ${filter === f.value ? 'opacity-50' : 'text-[#C8C7BF]'}`}>
                  {f.value === 'all' ? stats.total
                    : f.value === 'not-started' ? stats.notStarted
                    : f.value === 'in-progress' ? stats.inProgress
                    : stats.done}
                </span>
              </button>
            ))}
          </div>

          {/* Filter prioritas + urut tanggal */}
          <div className="flex items-center gap-2">
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as typeof priorityFilter)}
              className="text-xs font-body text-[#6B6860] bg-white border border-[#E8E7E3] rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-[#C8C7BF] cursor-pointer"
            >
              <option value="all">Semua prioritas</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              value={dateSort}
              onChange={(e) => setDateSort(e.target.value as typeof dateSort)}
              className="text-xs font-body text-[#6B6860] bg-white border border-[#E8E7E3] rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-[#C8C7BF] cursor-pointer"
            >
              <option value="manual">Urutan manual</option>
              <option value="newest">Tanggal: Terbaru</option>
              <option value="oldest">Tanggal: Terlama</option>
            </select>
          </div>
        </div>

        {/* Quick add */}
        {!lock.locked && canModify && (
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 flex items-center gap-2.5 bg-white border border-[#E8E7E3] rounded-xl px-3.5 py-2.5 focus-within:border-[#C8C7BF] transition-colors">
            <Plus className="w-4 h-4 text-[#C0BDB6] flex-shrink-0" />
            <input
              type="text"
              value={quickTitle}
              onChange={(e) => setQuickTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAdd() }}
              placeholder="Tambah task cepat — ketik judul lalu Enter"
              className="flex-1 text-sm font-body text-[#1A1916] placeholder:text-[#C0BDB6] bg-transparent border-none outline-none"
            />
            {quickTitle.trim() && (
              <button
                onClick={handleQuickAdd}
                disabled={quickSaving}
                className="flex items-center gap-1.5 px-3 py-1 bg-[#1A1916] text-white rounded-lg text-xs font-medium font-body hover:bg-[#2A2A28] disabled:opacity-50 transition-all flex-shrink-0"
              >
                {quickSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Tambah'}
              </button>
            )}
          </div>
        </div>
        )}

        {/* Task list */}
        {filteredTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-12 h-12 rounded-2xl bg-white border border-[#E8E7E3] flex items-center justify-center mb-3">
              <CheckCircle2 className="w-5 h-5 text-[#C0BDB6]" />
            </div>
            <p className="text-[#8A8A85] font-body text-sm mb-1">
              {filter === 'all' ? 'Belum ada task' : `Tidak ada task ${filter.replace('-', ' ')}`}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {(showAllTasks ? filteredTasks : filteredTasks.slice(0, 3)).map((task, i) => (
                <div
                  key={task.id}
                  draggable={canReorder && dragArmId === task.id}
                  onDragStart={() => setDragIndex(i)}
                  onDragOver={(e) => { if (canReorder && dragIndex !== null) { e.preventDefault(); setOverIndex(i) } }}
                  onDrop={(e) => { e.preventDefault(); handleDrop() }}
                  onDragEnd={resetDrag}
                  className={`relative group/row transition-all ${dragIndex === i ? 'opacity-50' : ''} ${
                    overIndex === i && dragIndex !== null && dragIndex !== i ? 'ring-2 ring-[#6366F1] ring-offset-2 rounded-xl' : ''
                  }`}
                >
                  <TaskItem task={task} locked={lock.locked} canEdit={canEditTaskRow(task)} />
                  {canReorder && (
                    <button
                      onMouseDown={() => setDragArmId(task.id)}
                      onMouseUp={() => { if (dragIndex === null) setDragArmId(null) }}
                      title="Tarik untuk pindahkan urutan"
                      aria-label="Pindahkan task"
                      className="absolute top-1/2 -translate-y-1/2 -right-7 p-1 rounded-md text-[#C0BDB6] hover:text-[#6B6860] hover:bg-[#F4F3F0] cursor-grab active:cursor-grabbing opacity-0 group-hover/row:opacity-100 transition-opacity"
                    >
                      <Grip className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {filteredTasks.length > 3 && (
              <button
                onClick={() => setShowAllTasks((v) => !v)}
                className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-[#E8E7E3] bg-white text-[13px] font-body font-medium text-[#6B6860] hover:text-[#1A1916] hover:border-[#C8C7BF] transition-all"
              >
                {showAllTasks
                  ? 'Tampilkan lebih sedikit'
                  : `Lihat ${filteredTasks.length - 3} task lainnya`}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAllTasks ? 'rotate-180' : ''}`} />
              </button>
            )}
          </>
        )}
      </main>

      {showCreateTask && (
        <CreateTaskModal projectId={projectId} onClose={() => setShowCreateTask(false)} />
      )}

      {showEditProject && (
        <EditProjectModal project={project} onClose={() => setShowEditProject(false)} />
      )}
    </div>
  )
}
